import { DragEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as fabric from 'fabric';
import { useFabricJSEditor } from 'fabricjs-react';
import { useFlash } from 'contexts/flash';
import { useAsyncCallback } from 'hooks/useAsyncCallback';
import {
  CLICK_DRAG_THRESHOLD,
  DND_MIME,
  FloorPlanToolType,
  MARKER_RADIUS,
  MAX_ZOOM,
  MIN_ZOOM,
  ZOOM_STEP
} from 'const/floorPlan';
import {
  createFloorPlanIssue,
  deleteFloorPlanIssue,
  getFloorPlanIssues,
  updateFloorPlanIssue
} from 'services/locations';
import {
  InspectionIssue,
  IssueMarker,
  IssueModalState,
  Point,
  Size,
  ViewState
} from '../../../../../../types/issue';
import {
  clamp,
  clampPan,
  colorForStatus,
  fitBaseSize,
  imageBox,
  pointToPct
} from 'utils/floorPlanGeometry';
import { createMarkerDeleteControl } from 'utils/floorPlanMarkerControl';

const mapApiIssues = (issues: InspectionIssue[]): IssueMarker[] =>
  issues
    .map((issue) => ({
      id: String(issue.id),
      issueId: issue.id,
      xPct: parseFloat(issue.xAxis) * 100,
      yPct: parseFloat(issue.yAxis) * 100,
      details: issue.description ?? issue.notes ?? '',
      status: issue.status
    }))
    // An issue without usable coordinates cannot be drawn, so leave it out.
    .filter((marker) => !Number.isNaN(marker.xPct) && !Number.isNaN(marker.yPct));

const clientPoint = (e: Event): Point | null =>
  'clientX' in e ? { x: (e as MouseEvent).clientX, y: (e as MouseEvent).clientY } : null;

export const useInjection = (floorPlanId?: number) => {
  const { setFlash } = useFlash();
  const { editor, onReady } = useFabricJSEditor();

  const [zoom, setZoom] = useState<number>(MIN_ZOOM);
  const [pan, setPan] = useState<Point>({ x: 0, y: 0 });
  const [markers, setMarkers] = useState<IssueMarker[]>([]);
  const [containerSize, setContainerSize] = useState<Size>({ w: 0, h: 0 });
  const [naturalSize, setNaturalSize] = useState<Size>({ w: 0, h: 0 });
  const [issueModal, setIssueModal] = useState<IssueModalState>();
  const [isDropTarget, setIsDropTarget] = useState<boolean>(false);
  const [pendingDeleteId, setPendingDeleteId] = useState<string>();
  const [isLoading, setisLoading] = useState<boolean>(true);
  const [issues, setissues] = useState<InspectionIssue[]>([]);
  const [total, settotal] = useState<number>(0);

  const canvasRef = useRef<HTMLDivElement>(null);
  const dragGhostRef = useRef<HTMLDivElement>(null);

  const scale = zoom / 100;

  const base = useMemo(() => fitBaseSize(containerSize, naturalSize), [containerSize, naturalSize]);

  const view: ViewState = useMemo(
    () => ({ pan, scale, base, container: containerSize }),
    [pan, scale, base, containerSize]
  );

  // Fabric's event handlers are registered once, so everything they read lives
  // behind a ref instead of closing over a stale render.
  const viewRef = useRef<ViewState>(view);
  const markersRef = useRef<IssueMarker[]>(markers);
  const panDragRef = useRef<{ startX: number; startY: number; origin: Point }>();
  const markerDragRef = useRef<{ id: string; startX: number; startY: number; moved: boolean }>();
  const handlersRef = useRef<{
    onMarkerClick: (id: string) => void;
    onMarkerMoved: (id: string, xPct: number, yPct: number) => void;
    onMarkerDelete: (id: string) => void;
  }>();

  viewRef.current = view;
  markersRef.current = markers;

  const handleGetIssues = useCallback(async () => {
    if (!floorPlanId) return;
    const data = await getFloorPlanIssues(floorPlanId);
    setMarkers(mapApiIssues(data?.issues ?? []));
    setissues(data?.issues ?? []);
    settotal(data.total);
  }, [floorPlanId]);

  const { asyncCallback: asyncGetIssues } = useAsyncCallback(handleGetIssues, [handleGetIssues]);

  useEffect(() => {
    asyncGetIssues();
  }, [asyncGetIssues]);

  // Track the canvas container's available size.
  useEffect(() => {
    const element = canvasRef.current;
    if (!element) return;

    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setContainerSize({ w: width, h: height });
    });
    observer.observe(element);

    return () => observer.disconnect();
  }, []);

  // Keep the pan within bounds whenever zoom/size changes (also re-centres at 100%).
  useEffect(() => {
    setPan((previous) => clampPan(previous.x, previous.y, viewRef.current));
  }, [zoom, base.w, base.h, containerSize.w, containerSize.h]);

  const zoomIn = useCallback(() => {
    setZoom((previous) => clamp(previous + ZOOM_STEP, MIN_ZOOM, MAX_ZOOM));
  }, []);

  const zoomOut = useCallback(() => {
    setZoom((previous) => clamp(previous - ZOOM_STEP, MIN_ZOOM, MAX_ZOOM));
  }, []);

  const fitToScreen = useCallback(() => {
    setZoom(MIN_ZOOM);
    setPan({ x: 0, y: 0 });
  }, []);

  // Scroll-wheel zoom (updates the toolbar too). A native, non-passive listener
  // is required so the page scroll can be prevented while zooming.
  useEffect(() => {
    const element = canvasRef.current;
    if (!element) return;

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      const direction = e.deltaY < 0 ? 1 : -1;
      setZoom((previous) => clamp(previous + direction * ZOOM_STEP, MIN_ZOOM, MAX_ZOOM));
    };

    element.addEventListener('wheel', handleWheel, { passive: false });

    return () => element.removeEventListener('wheel', handleWheel);
  }, []);

  const notifyFailure = useCallback(
    (message: string) => setFlash({ message, type: 'error' }),
    [setFlash]
  );
  // ---- Marker CRUD -----------------------------------------------------------
  // Markers update optimistically so dragging stays responsive; the request is
  // fired afterwards and only reported when it fails.
  const addMarkerAt = useCallback(
    async (xPct: number, yPct: number) => {
      if (!floorPlanId) return;
      const id = `marker-${Date.now()}`;
      setMarkers((previous) => [
        ...previous,
        { id, issueId: null, xPct, yPct, details: '', status: 'OPEN' }
      ]);

      try {
        const data = await createFloorPlanIssue(floorPlanId, {
          xAxis: xPct / 100,
          yAxis: yPct / 100
        });
        const issueId: number | undefined = data?.id ?? data?.issue?.id;
        if (issueId == null) return;
        setMarkers((previous) =>
          previous.map((marker) => (marker.id === id ? { ...marker, issueId } : marker))
        );
      } catch (error) {
        setMarkers((previous) => previous.filter((marker) => marker.id !== id));
        notifyFailure('Failed to create the issue.');
      }
    },
    [floorPlanId, notifyFailure]
  );

  const removeMarker = useCallback(
    async (id: string) => {
      const marker = markersRef.current.find((item) => item.id === id);
      if (!marker) return;

      setMarkers((previous) => previous.filter((item) => item.id !== id));
      if (!floorPlanId || marker.issueId == null) return;

      try {
        await deleteFloorPlanIssue(floorPlanId, marker.issueId);
        await handleGetIssues();
      } catch (error) {
        setMarkers((previous) => [...previous, marker]);
        notifyFailure('Failed to delete the issue.');
      }
    },
    [floorPlanId, notifyFailure]
  );

  // Deleting is confirmed first - the delete badge sits on the marker itself and
  // is easy to hit by accident while dragging.
  const requestMarkerDelete = useCallback((id: string) => setPendingDeleteId(id), []);

  const cancelMarkerDelete = useCallback(() => setPendingDeleteId(undefined), []);

  const confirmMarkerDelete = useCallback(() => {
    if (pendingDeleteId) removeMarker(pendingDeleteId);
    setPendingDeleteId(undefined);
  }, [pendingDeleteId, removeMarker]);

  const persistMarker = useCallback(
    async (marker: IssueMarker) => {
      if (!floorPlanId || marker.issueId == null) return;

      try {
        await updateFloorPlanIssue(floorPlanId, marker.issueId, {
          xAxis: marker.xPct / 100,
          yAxis: marker.yPct / 100,
          description: marker.details
        });
        await handleGetIssues();
      } catch (error) {
        notifyFailure('Failed to save the issue.');
      }
    },
    [floorPlanId, notifyFailure]
  );

  // Dropping a marker persists its new position straight away - no need to open
  // the modal and submit just to move an issue.
  const moveMarker = useCallback(
    (id: string, xPct: number, yPct: number) => {
      const marker = markersRef.current.find((item) => item.id === id);
      if (!marker) return;

      const moved = { ...marker, xPct, yPct };
      setMarkers((previous) => previous.map((item) => (item.id === id ? moved : item)));
      persistMarker(moved);
    },
    [persistMarker]
  );

  const openIssueModal = useCallback((id: string) => {
    const marker = markersRef.current.find((item) => item.id === id);
    if (!marker) return;

    setIssueModal({
      visible: true,
      id: marker.id,
      xPct: Math.round(marker.xPct * 10) / 10,
      yPct: Math.round(marker.yPct * 10) / 10,
      text: marker.details
    });
  }, []);

  handlersRef.current = {
    onMarkerClick: openIssueModal,
    onMarkerMoved: moveMarker,
    onMarkerDelete: requestMarkerDelete
  };

  // ---- Fabric marker layer ---------------------------------------------------

  const deleteControl = useMemo(
    () =>
      createMarkerDeleteControl((id) => {
        // The pointer-up also ends the marker drag; clear it so it isn't
        // mistaken for a click that would re-open the modal.
        markerDragRef.current = undefined;
        if (id) handlersRef.current?.onMarkerDelete(id);
      }),
    []
  );

  // The canvas itself is created and disposed by <FabricJSCanvas>; this only
  // configures it and wires the pointer interactions.
  useEffect(() => {
    const canvas = editor?.canvas;
    if (!canvas) return;

    canvas.selection = false;
    canvas.preserveObjectStacking = true;
    canvas.defaultCursor = 'grab';
    canvas.hoverCursor = 'grab';

    canvas.on('mouse:down', (opt) => {
      const point = clientPoint(opt.e);
      if (!point) return;

      const id = opt.target?.id;
      if (id) {
        markerDragRef.current = { id, startX: point.x, startY: point.y, moved: false };
        return;
      }
      // Empty canvas: panning is always available.
      panDragRef.current = { startX: point.x, startY: point.y, origin: viewRef.current.pan };
    });

    canvas.on('mouse:move', (opt) => {
      const drag = panDragRef.current;
      if (drag) {
        const point = clientPoint(opt.e);
        if (!point) return;
        setPan(
          clampPan(
            drag.origin.x + (point.x - drag.startX),
            drag.origin.y + (point.y - drag.startY),
            viewRef.current
          )
        );
        return;
      }
      if (markerDragRef.current) return;

      const active = canvas.getActiveObject();
      if (opt.target && opt.target !== active) {
        canvas.setActiveObject(opt.target);
        canvas.requestRenderAll();
      } else if (!opt.target && active) {
        canvas.discardActiveObject();
        canvas.requestRenderAll();
      }

      const upperCanvas = canvas.upperCanvasEl;
      if (upperCanvas) {
        upperCanvas.title = opt.target?.description || '';
      }
    });

    canvas.on('object:moving', (opt) => {
      const object = opt.target;
      const drag = markerDragRef.current;
      const box = imageBox(viewRef.current);

      object.set({
        left: clamp(object.left, box.left + MARKER_RADIUS, box.left + box.w - MARKER_RADIUS),
        top: clamp(object.top, box.top + MARKER_RADIUS, box.top + box.h - MARKER_RADIUS)
      });

      // Ignore tiny movements so a click isn't mistaken for a drag.
      const point = clientPoint(opt.e);
      if (
        drag &&
        point &&
        Math.hypot(point.x - drag.startX, point.y - drag.startY) > CLICK_DRAG_THRESHOLD
      ) {
        drag.moved = true;
        // Hide the delete badge for the rest of the drag - it's a hover
        // affordance, and dragging it along with the circle looks broken.
        object.hasControls = false;
      }
    });

    canvas.on('mouse:up', (opt) => {
      panDragRef.current = undefined;
      const drag = markerDragRef.current;
      markerDragRef.current = undefined;
      if (!drag) return;

      // Bring the badge back once the drag ends (the pointer may have been
      // released off the circle, so look it up by id rather than by target).
      const dragged = canvas.getObjects().find((object) => object.id === drag.id);
      if (dragged) dragged.hasControls = true;

      if (!drag.moved) {
        handlersRef.current?.onMarkerClick(drag.id);
        return;
      }

      const object = opt.target;
      if (!object) return;
      const pct = pointToPct(object.left, object.top, viewRef.current);
      if (pct) handlersRef.current?.onMarkerMoved(drag.id, pct.xPct, pct.yPct);
    });

    return () => {
      canvas.off('mouse:down');
      canvas.off('mouse:move');
      canvas.off('mouse:up');
      canvas.off('object:moving');
    };
  }, [editor]);

  // Sync fabric objects with the markers, and reposition them for the current
  // pan/zoom. Positions are screen pixels; only the circle's centre moves with
  // zoom, its radius never does.
  useEffect(() => {
    const canvas = editor?.canvas;
    if (!canvas) return;

    canvas.setDimensions({ width: containerSize.w, height: containerSize.h });

    const existing = new Map(canvas.getObjects().map((object) => [object.id, object]));

    existing.forEach((object, id) => {
      if (!markers.some((marker) => marker.id === id)) canvas.remove(object);
    });

    const box = imageBox({ pan, scale, base, container: containerSize });

    markers.forEach((marker) => {
      const fill = colorForStatus(marker.status);
      let circle = existing.get(marker.id) as fabric.Circle | undefined;

      if (!circle) {
        circle = new fabric.Circle({
          radius: MARKER_RADIUS,
          fill,
          stroke: '#FFFFFF',
          strokeWidth: 2,
          originX: 'center',
          originY: 'center',
          hasBorders: false,
          lockRotation: true,
          lockScalingX: true,
          lockScalingY: true,
          hoverCursor: 'grab',
          moveCursor: 'grabbing',
          objectCaching: false
        });
        circle.id = marker.id;
        circle.controls = { delete: deleteControl };
        canvas.add(circle);
      }

      circle.set({
        left: box.left + (marker.xPct / 100) * box.w,
        top: box.top + (marker.yPct / 100) * box.h,
        fill,
        description: marker.details
      });
      circle.setCoords();
    });

    canvas.requestRenderAll();
  }, [editor, markers, pan, scale, base, containerSize, deleteControl]);

  // Leaving the canvas altogether also clears the hovered marker.
  const handleStageMouseLeave = useCallback(() => {
    const canvas = editor?.canvas;
    if (!canvas || markerDragRef.current || !canvas.getActiveObject()) return;
    canvas.discardActiveObject();
    canvas.requestRenderAll();
  }, [editor]);

  // ---- Issue modal -----------------------------------------------------------

  const handleIssueTextChange = useCallback((text: string) => {
    setIssueModal((previous) => (previous ? { ...previous, text } : previous));
  }, []);

  const closeIssueModal = useCallback(() => setIssueModal(undefined), []);

  const handleIssueSubmit = useCallback(() => {
    if (!issueModal) return;

    const marker = markersRef.current.find((item) => item.id === issueModal.id);
    setIssueModal(undefined);
    if (!marker) return;

    const edited = { ...marker, details: issueModal.text };
    setMarkers((previous) => previous.map((item) => (item.id === edited.id ? edited : item)));
    persistMarker(edited);
  }, [issueModal, persistMarker]);

  // ---- Sidebar drag-and-drop -------------------------------------------------

  const handleToolDragStart = useCallback(
    (e: DragEvent<HTMLDivElement>, toolId: FloorPlanToolType) => {
      e.dataTransfer.setData(DND_MIME, toolId);
      e.dataTransfer.effectAllowed = 'copy';
      // Drag the marker itself, not a snapshot of the sidebar row.
      const ghost = dragGhostRef.current;
      if (ghost) e.dataTransfer.setDragImage(ghost, MARKER_RADIUS, MARKER_RADIUS);
    },
    []
  );

  const handleToolDragEnd = useCallback(() => setIsDropTarget(false), []);

  const handleDragOver = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      if (!e.dataTransfer.types.includes(DND_MIME)) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
      if (!isDropTarget) setIsDropTarget(true);
    },
    [isDropTarget]
  );

  const handleDragLeave = useCallback((e: DragEvent<HTMLDivElement>) => {
    // Ignore moves between children of the canvas.
    if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
    setIsDropTarget(false);
  }, []);

  const handleDrop = useCallback(
    async (e: DragEvent<HTMLDivElement>) => {
      if (!e.dataTransfer.types.includes(DND_MIME)) return;
      e.preventDefault();
      setIsDropTarget(false);

      // Drop coordinates are relative to the canvas box the markers are drawn in.
      const rect = e.currentTarget.getBoundingClientRect();
      const pct = pointToPct(e.clientX - rect.left, e.clientY - rect.top, viewRef.current);
      if (pct) {
        await addMarkerAt(pct.xPct, pct.yPct);
        await handleGetIssues();
      }
    },
    [addMarkerAt, handleGetIssues]
  );

  const canPan = base.w * scale > containerSize.w || base.h * scale > containerSize.h;

  return {
    zoom,
    pan,
    scale,
    base,
    issueModal,
    isDropTarget,
    isDeleteConfirmOpen: pendingDeleteId != null,
    canPan,
    canvasRef,
    dragGhostRef,
    onReady,
    setNaturalSize,
    zoomIn,
    zoomOut,
    fitToScreen,
    handleStageMouseLeave,
    cancelMarkerDelete,
    confirmMarkerDelete,
    handleIssueTextChange,
    closeIssueModal,
    handleIssueSubmit,
    handleToolDragStart,
    handleToolDragEnd,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    isLoading,
    setisLoading,
    issues,
    total
  };
};
